import React, { useEffect, useRef, useState } from 'react';
import { Save, Loader2, Eye, EyeOff, ExternalLink, Upload, X, Image as ImageIcon } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { adminApi, uploadToCloudinary, getImageUrl } from '../../lib/api';

// Public URL each page_id maps to for the live preview iframe
const PAGE_PREVIEW_URLS = {
  home: '/',
  glam_booth: '/glam-booth',
  photography: '/photography',
  videography: '/videography',
  our_work: '/previous-events',
  about: '/about',
  contact: '/contact',
};

// Schema for each page — matches the actual live page content.
// field types: text (default) | textarea | image
const PAGE_SCHEMAS = {
  home: {
    label: 'Home',
    fields: [
      { key: 'hero_image', label: 'Hero Background Photo', type: 'image' },
      { key: 'hero_eyebrow', label: 'Hero Eyebrow (small gold text)', placeholder: 'Glam Booth · Photography · Videography' },
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'Cinematic memories, beautifully captured.' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', placeholder: 'Mayden Films brings a premium Glam Booth, event photography and cinematic videography…' },
      { key: 'hero_button_primary', label: 'Primary Button', placeholder: 'Check Your Event Date' },
      { key: 'hero_button_secondary', label: 'Secondary Button', placeholder: 'View the Glam Booth' },
      { key: 'intro_title', label: 'Intro Section Title', placeholder: 'Beautifully crafted, personally delivered' },
      { key: 'intro_paragraph', label: 'Intro Paragraph', type: 'textarea' },
      { key: 'services_eyebrow', label: 'Services Eyebrow', placeholder: 'What we offer' },
      { key: 'services_title', label: 'Services Section Title', placeholder: 'Four ways to capture your celebration' },
      { key: 'services_subtitle', label: 'Services Subtitle', type: 'textarea' },
      { key: 'booking_title', label: 'How-Booking-Works Title', placeholder: 'How booking works' },
      { key: 'booking_subtitle', label: 'How-Booking-Works Subtitle', placeholder: 'A simple, transparent process from enquiry to gallery.' },
      { key: 'recent_title', label: 'Recent Events Title', placeholder: 'Recent events' },
      { key: 'recent_subtitle', label: 'Recent Events Subtitle', placeholder: 'A glimpse of recent celebrations.' },
      { key: 'why_title', label: 'Why-Choose Title', placeholder: 'Why choose Mayden Films' },
      { key: 'cta_title', label: 'Bottom CTA Title', placeholder: 'Planning an event?' },
      { key: 'cta_subtitle', label: 'Bottom CTA Subtitle', type: 'textarea', placeholder: "Tell me the date, venue and celebration, and I'll confirm availability." },
      { key: 'cta_button', label: 'Bottom CTA Button', placeholder: 'Check Your Event Date' },
    ],
  },
  glam_booth: {
    label: 'Glam Booth',
    fields: [
      { key: 'hero_image', label: 'Hero Background Photo (optional)', type: 'image' },
      { key: 'hero_eyebrow', label: 'Hero Eyebrow', placeholder: 'The Glam Booth' },
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'An unforgettable guest experience' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'shared_title', label: 'Shared Inclusions Heading', placeholder: 'Every Glam Booth package includes' },
      { key: 'mm_title', label: 'Mayden Messages Title', placeholder: 'A cinematic video guestbook' },
      { key: 'mm_subtitle', label: 'Mayden Messages Description', type: 'textarea' },
      { key: 'cta_title', label: 'Bottom CTA Title', placeholder: 'Ready to book your Glam Booth?' },
    ],
  },
  photography: {
    label: 'Photography',
    fields: [
      { key: 'hero_image', label: 'Hero Background Photo', type: 'image' },
      { key: 'hero_eyebrow', label: 'Hero Eyebrow', placeholder: 'Selected Premium Service' },
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'Event Photography' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'hero_price', label: 'Hero Price', placeholder: 'from $749' },
      { key: 'hero_button', label: 'Hero Button', placeholder: 'Request a Custom Quote' },
      { key: 'expect_title', label: '"What to Expect" Title', placeholder: 'What to expect' },
      { key: 'bottom_cta_title', label: 'Bottom CTA Title', placeholder: "Let's discuss your coverage" },
      { key: 'bottom_cta_subtitle', label: 'Bottom CTA Subtitle', type: 'textarea' },
    ],
  },
  videography: {
    label: 'Videography',
    fields: [
      { key: 'hero_image', label: 'Hero Background Photo', type: 'image' },
      { key: 'hero_eyebrow', label: 'Hero Eyebrow', placeholder: 'Selected Premium Service' },
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'Event Videography' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'hero_price', label: 'Hero Price', placeholder: 'from $1,299' },
      { key: 'hero_button', label: 'Hero Button', placeholder: 'Request a Custom Quote' },
      { key: 'expect_title', label: '"What to Expect" Title', placeholder: 'What to expect' },
      { key: 'bottom_cta_title', label: 'Bottom CTA Title', placeholder: "Let's plan your film" },
      { key: 'bottom_cta_subtitle', label: 'Bottom CTA Subtitle', type: 'textarea' },
    ],
  },
  our_work: {
    label: 'Portfolio',
    fields: [
      { key: 'hero_title_line1', label: 'Hero Title — Line 1', placeholder: 'Recent' },
      { key: 'hero_title_line2', label: 'Hero Title — Line 2 (gold)', placeholder: 'Events' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'hero_subtitle_note', label: 'Hero Note (gold)', placeholder: 'Images are shared with permission.' },
    ],
  },
  about: {
    label: 'About',
    fields: [
      { key: 'portrait_image', label: 'Portrait / Feature Photo', type: 'image' },
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'About Mayden Films' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'story_p1', label: 'Story Paragraph 1', type: 'textarea' },
      { key: 'story_p2', label: 'Story Paragraph 2', type: 'textarea' },
      { key: 'story_p3', label: 'Story Paragraph 3', type: 'textarea' },
      { key: 'story_p4', label: 'Story Paragraph 4', type: 'textarea' },
      { key: 'story_p5', label: 'Story Paragraph 5', type: 'textarea' },
      { key: 'areas_title', label: '"Where I Work" Title', placeholder: 'Where I work' },
      { key: 'cta_title', label: 'Bottom CTA Title', placeholder: "Let's make your event unforgettable" },
    ],
  },
  contact: {
    label: 'Contact',
    fields: [
      { key: 'hero_title', label: 'Hero Headline', placeholder: 'Contact Mayden Films' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'card_title', label: 'CTA Card Title', placeholder: 'Ready to check your date?' },
      { key: 'card_subtitle', label: 'CTA Card Subtitle', type: 'textarea' },
      { key: 'card_button', label: 'CTA Card Button', placeholder: 'Check Your Event Date' },
    ],
  },
};

const ImageField = ({ value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadToCloudinary(file, 'maydenfilms/pages', 'image');
      onChange(url);
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
      {value ? (
        <div className="relative group w-full max-w-sm">
          <img src={getImageUrl(value, 'thumb')} alt="" className="w-full h-40 object-cover rounded-lg border border-[#C5A880]/20" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full p-1.5 transition-colors"
            data-testid={`${testid}-remove`}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="w-full max-w-sm h-40 rounded-lg border border-dashed border-[#C5A880]/30 flex flex-col items-center justify-center text-[#EADBC8]/40 bg-[#1A1612]">
          <ImageIcon size={28} />
          <span className="text-xs mt-2">No photo — using the default</span>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
        data-testid={`${testid}-upload`}
      >
        {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
        {uploading ? 'Uploading…' : value ? 'Replace photo' : 'Upload photo'}
      </Button>
    </div>
  );
};

const PageEditor = ({ pageId, schema, previewOpen }) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);
  const iframeReady = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    iframeReady.current = false;
    adminApi
      .getPageContent(pageId)
      .then((doc) => setContent(doc.content || {}))
      .catch(() => setContent({}))
      .finally(() => setLoading(false));
  }, [pageId]);

  // Listen for iframe ready handshake, then push current content
  useEffect(() => {
    const handler = (e) => {
      const data = e.data || {};
      if (data.type === 'mf_preview_ready' && data.pageId === pageId) {
        iframeReady.current = true;
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { type: 'mf_preview', pageId, content },
            '*'
          );
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [pageId, content]);

  // Stream content changes to the iframe on every keystroke (when ready)
  useEffect(() => {
    if (iframeReady.current && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'mf_preview', pageId, content },
        '*'
      );
    }
  }, [content, pageId]);

  const update = (key, value) => setContent((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updatePageContent(pageId, content);
      toast({ title: 'Page content saved' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-[#C5A880] animate-spin" size={32} />
      </div>
    );

  const previewUrl = PAGE_PREVIEW_URLS[pageId] || '/';

  return (
    <div className={`grid gap-6 ${previewOpen ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
      {/* Editor column */}
      <Card className="bg-[#2A2420] border-[#C5A880]/20">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between pb-3 mb-2 border-b border-[#C5A880]/10">
            <p className="text-[#EADBC8]/60 text-xs uppercase tracking-widest">
              Editing — {schema.label}
            </p>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[#C5A880] hover:text-[#D4BC95] text-xs flex items-center gap-1"
              data-testid={`open-${pageId}-new-tab`}
            >
              <ExternalLink size={12} /> Open in new tab
            </a>
          </div>
          {schema.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">{f.label}</label>
              {f.type === 'image' ? (
                <ImageField
                  value={content[f.key] || ''}
                  onChange={(url) => update(f.key, url)}
                  testid={`pc-${pageId}-${f.key}`}
                />
              ) : f.type === 'textarea' ? (
                <Textarea
                  value={content[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={3}
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] placeholder:text-[#EADBC8]/30"
                  data-testid={`pc-${pageId}-${f.key}`}
                />
              ) : (
                <Input
                  value={content[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] placeholder:text-[#EADBC8]/30"
                  data-testid={`pc-${pageId}-${f.key}`}
                />
              )}
              {f.type !== 'image' && f.placeholder && !content[f.key] && (
                <p className="text-[#EADBC8]/30 text-xs mt-1">Default: "{f.placeholder}"</p>
              )}
            </div>
          ))}

          <div className="pt-4 border-t border-[#C5A880]/20">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
              data-testid={`save-${pageId}-btn`}
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : `Save ${schema.label} Page`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live preview column */}
      {previewOpen && (
        <Card className="bg-[#2A2420] border-[#C5A880]/20 lg:sticky lg:top-6 self-start">
          <CardContent className="p-3">
            <div className="flex items-center justify-between px-2 py-1 mb-2">
              <p className="text-[#EADBC8]/60 text-xs uppercase tracking-widest">
                Live preview · unsaved
              </p>
              <span className="text-[#C5A880] text-xs">{previewUrl}</span>
            </div>
            <div className="bg-[#1A1612] border border-[#C5A880]/10 rounded-md overflow-hidden">
              <iframe
                ref={iframeRef}
                key={pageId}
                src={previewUrl}
                title={`Preview ${schema.label}`}
                className="w-full h-[80vh] border-0 bg-[#1F1A17]"
                data-testid={`preview-iframe-${pageId}`}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const PageContentAdmin = () => {
  const [previewOpen, setPreviewOpen] = useState(true);

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#EADBC8] mb-2">Page Content</h1>
          <p className="text-[#EADBC8]/60">Edit the text and photos on every page of your site. Empty text fields use the default shown below each input; empty photo fields use the built-in default image.</p>
        </div>
        <Button
          onClick={() => setPreviewOpen((v) => !v)}
          variant="outline"
          className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 self-start"
          data-testid="toggle-preview-btn"
        >
          {previewOpen ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
          {previewOpen ? 'Hide live preview' : 'Show live preview'}
        </Button>
      </div>

      <Tabs defaultValue="home">
        <TabsList className="bg-[#2A2420] border border-[#C5A880]/20 mb-6 flex flex-wrap h-auto">
          {Object.entries(PAGE_SCHEMAS).map(([id, s]) => (
            <TabsTrigger
              key={id}
              value={id}
              className="data-[state=active]:bg-[#C5A880] data-[state=active]:text-[#1F1A17] text-[#EADBC8]/70"
              data-testid={`tab-${id}`}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(PAGE_SCHEMAS).map(([id, schema]) => (
          <TabsContent key={id} value={id}>
            <PageEditor pageId={id} schema={schema} previewOpen={previewOpen} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default PageContentAdmin;
